<template>
  <Transition name="fade">
    <div
      v-if="showSuggestions && filteredDomains.length > 0"
      class="email-suggestions"
      @mousedown.prevent
    >
      <div
        v-for="domain in filteredDomains"
        :key="domain"
        class="email-suggestion-item"
        @click="selectDomain(domain)"
      >
        <span class="email-local-part">{{ localPart }}</span>
        <span class="email-domain-part">@{{ domain }}</span>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// List of popular email providers extracted from the provided HTML
const EMAIL_DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'naver.com',
  'mail.ru',
  '163.com',
  'icloud.com',
  'yahoo.com',
  'qq.com',
  'protonmail.com',
  'yandex.ru',
  'web.de',
  'proton.me',
  'gmx.de',
  'yahoo.com.tw',
  'rambler.ru',
  'triots.com',
  'hanmail.net',
  'outlook.es',
  'exdonuts.com'
]

const props = defineProps<{
  email: string
  isFocused: boolean
}>()

const emit = defineEmits<{
  (e: 'select', email: string): void
}>()

// Extract the local part (before @) and what user typed after @
const emailParts = computed(() => {
  const atIndex = props.email.indexOf('@')
  if (atIndex === -1) {
    return { localPart: props.email, domainInput: '' }
  }
  return {
    localPart: props.email.substring(0, atIndex),
    domainInput: props.email.substring(atIndex + 1).toLowerCase()
  }
})

const localPart = computed(() => emailParts.value.localPart)

// Show suggestions only when:
// - Input is focused
// - Email contains @
// - There's a local part before @
// - Domain part is incomplete (doesn't have a dot after some text, indicating a complete domain)
const showSuggestions = computed(() => {
  const { localPart, domainInput } = emailParts.value
  if (!props.isFocused) return false
  if (!props.email.includes('@')) return false
  if (!localPart) return false
  // Hide if user has typed what looks like a complete domain (has text after the dot)
  const dotIndex = domainInput.indexOf('.')
  if (dotIndex !== -1 && domainInput.length > dotIndex + 2) return false
  return true
})

// Filter domains based on what user typed after @
const filteredDomains = computed(() => {
  const { domainInput } = emailParts.value
  if (!domainInput) return EMAIL_DOMAINS
  return EMAIL_DOMAINS.filter(domain => 
    domain.toLowerCase().startsWith(domainInput)
  )
})

const selectDomain = (domain: string) => {
  const fullEmail = `${localPart.value}@${domain}`
  emit('select', fullEmail)
}
</script>

<style scoped lang="scss">
.email-suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10;
  background: var(--hm-surface);
  border: 1px solid var(--hm-border);
  border-radius: var(--ds-radius-card);
  box-shadow: var(--hm-shadow-soft);
  overflow: auto;
  max-height: 280px;
  padding: 8px;
}

.email-suggestion-item {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 48px;
  padding: 0 6px;
  border-radius: var(--ds-radius-control);
  font-size: var(--ds-text-size-body-lg);
  line-height: 22px;
  font-weight: var(--ds-weight-medium);
  cursor: pointer;
  transition: background 0.15s;
  
  &:hover {
    background: var(--hm-surface-alt);
  }
}

.email-local-part {
  color: var(--hm-text-muted);
}

.email-domain-part {
  color: var(--hm-text-primary);
}

// Transition animation
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Mobile styles
@media (max-width: 768px) {
  .email-suggestion-item {
    font-size: var(--ds-text-size-body);
  }
  
  .email-suggestions {
    border-radius: var(--ds-radius-control);
  }
}
</style>
